/**
 * SSE client for real-time document signing events.
 *
 * Connects to GET /api/documents/:documentId/events using the browser's
 * native EventSource API. Auth is passed via ?token= query param because
 * EventSource can't set custom headers — the backend's extractToken()
 * middleware already supports this pattern.
 */

export interface DocumentEvent {
  type: 'CONNECTED' | 'FIELD_SIGNED' | 'FIELD_UPDATED' | 'ALL_SIGNED';
  documentId?: string;
  fieldId?: string;
  fieldType?: string;
  signedBy?: string;
  signedAt?: string;
  status?: string;
  signedDocumentFileId?: string;
  projectId?: string;
  versionId?: string | null;
  versionNumber?: number;
}

type EventHandler = (event: DocumentEvent) => void;

class DocumentEventService {
  private source: EventSource | null = null;
  private listeners = new Map<string, EventHandler[]>();

  /**
   * Connect to a document's SSE event stream.
   * Closes any existing connection first.
   */
  connect(documentId: string, token: string | null): void {
    this.disconnect();

    const url = token
      ? `/api/documents/${documentId}/events?token=${encodeURIComponent(token)}`
      : `/api/documents/${documentId}/events`;

    this.source = new EventSource(url);

    this.source.onmessage = (e) => {
      try {
        const event: DocumentEvent = JSON.parse(e.data);
        this.dispatch(event.type, event);
      } catch {
        // skip malformed events
      }
    };

    this.source.onerror = () => {
      // EventSource auto-reconnects — no manual intervention needed.
      // The 'error' event fires on both transient network issues
      // and permanent failures (e.g. 401). EventSource handles retries.
    };
  }

  /** Close the SSE connection and clear all listeners. */
  disconnect(): void {
    this.source?.close();
    this.source = null;
    this.listeners.clear();
  }

  /** Register a handler for a specific event type. */
  on(eventType: string, handler: EventHandler): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType)!.push(handler);
  }

  /** Remove a specific handler for an event type. */
  off(eventType: string, handler: EventHandler): void {
    const handlers = this.listeners.get(eventType);
    if (!handlers) return;
    this.listeners.set(eventType, handlers.filter((h) => h !== handler));
  }

  /** Whether the connection is currently open. */
  get connected(): boolean {
    return this.source?.readyState === EventSource.OPEN;
  }

  private dispatch(eventType: string, event: DocumentEvent): void {
    this.listeners.get(eventType)?.forEach((h) => h(event));
  }
}

/** Singleton instance — one SSE connection at a time. */
export const documentEventService = new DocumentEventService();
